import {Search} from 'lucide-react';
import {useMemo, useState} from "react";
import {PhoneHQInput, PhoneHQGlyph, PhoneHQLoader} from 'ch3-ui-lib';
import {useListEnsAlertTemplatesQuery} from "../../../store/features/ens/ensService";
import {EnsAlertTemplate} from "../../../store/features/ens/types";
import {ChannelBadge} from "../components/ChannelBadge";
import {TypeBadge} from "../components/TypeBadge";
import {filterTemplates, templateKey} from "../Templates/templateUtils";
import {formatDuration, formatTargetSummary} from "./utils";

interface Props {
    onClose: () => void;
    onManageTemplates: () => void;
    onUseTemplate: (template: EnsAlertTemplate) => void;
}

const targetSummary = (template: EnsAlertTemplate): string =>
    formatTargetSummary(template.targetGroupIds.length, Math.max(template.targetUsers.length, template.targetUserIds.length));

const levelPreview = (template: EnsAlertTemplate): string => {
    if (template.levels.length === 0) return 'No escalation levels';

    const channels = template.levels
        .slice(0, 2)
        .map((level) => level.channel.replace('_', ' ').toLowerCase())
        .join(', ');
    const remaining = template.levels.length > 2 ? ` + ${template.levels.length - 2} more` : '';
    const totalDelay = template.levels.reduce((sum, level) => sum + level.delaySecondsFromPrevious, 0);

    return `${template.levels.length} levels: ${channels}${remaining}. Final after ${formatDuration(totalDelay)}.`;
};

const TemplateCard = ({
    template,
    onUseTemplate,
}: {
    template: EnsAlertTemplate;
    onUseTemplate: (template: EnsAlertTemplate) => void;
}) => (
    <button
        phq-id={`ensWizard-useTemplate-${templateKey(template)}`}
        type="button"
        onClick={() => onUseTemplate(template)}
        className="group flex w-full items-start gap-3 rounded-lg border border-line bg-surface px-3 py-3 text-left transition-colors hover:border-brand-focus hover:bg-brand-tint-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-focus"
    >
        <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
                <TypeBadge type={template.type} variant="dotText" className="!text-xs"/>
                <span className="rounded-md bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-ink-muted">
                    {template.system ? 'System' : 'Custom'}
                </span>
            </div>
            <div className="mt-1 line-clamp-1 text-sm font-semibold text-ink">{template.name}</div>
            <div className="mt-0.5 line-clamp-1 text-xs leading-5 text-ink-muted">
                {template.message || 'No message saved in this template.'}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted">
                {template.levels.slice(0, 3).map((level) => (
                    <ChannelBadge key={`${templateKey(template)}-${level.sequenceNo}`} channel={level.channel}/>
                ))}
                {template.levels.length > 3 && (
                    <span className="inline-flex h-6 items-center rounded-full border border-line px-2 text-[11px] font-semibold text-ink-muted">
                        +{template.levels.length - 3}
                    </span>
                )}
                <span className="line-clamp-1 min-w-[180px] flex-1">
                    {targetSummary(template)} · {levelPreview(template)}
                </span>
            </div>
        </div>
        <span className="mt-6 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-brand-ink transition-colors group-hover:bg-brand-tint">
            <PhoneHQGlyph name="keyboard_arrow_right" size={18}/>
        </span>
    </button>
);

export const TemplatePickerOverlay = ({onClose, onManageTemplates, onUseTemplate}: Props) => {
    const [phrase, setPhrase] = useState('');
    const {data: templates, isLoading} = useListEnsAlertTemplatesQuery();

    const filteredTemplates = useMemo(
        () => filterTemplates(templates ?? [], phrase, true),
        [phrase, templates],
    );

    return (
        <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 p-5 backdrop-blur-[2px]"
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="flex max-h-[82%] w-[600px] max-w-full flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-[0_20px_50px_rgba(0,0,0,0.22)]">
                <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-ink">Start from a template</div>
                        <div className="mt-0.5 text-xs leading-5 text-ink-muted">
                            Applies the name, escalation timeouts, target group and voice prompt.
                        </div>
                    </div>
                    <button
                        type="button"
                        aria-label="Close template picker"
                        onClick={onClose}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
                    >
                        <PhoneHQGlyph name="close" size={16}/>
                    </button>
                </div>

                <div className="border-b border-line px-4 py-3">
                    <PhoneHQInput
                        phq-id="ensWizard-templateSearch"
                        value={phrase}
                        placeholder="Search templates"
                        prefixIcon={<Search size={16} aria-hidden="true" className="!p-0"/>}
                        onChange={({target: {value: value}}) => setPhrase(value)} onClear={() => setPhrase('')}/>
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2">
                    {isLoading && (
                        <div className="flex items-center justify-center py-8">
                            <PhoneHQLoader/>
                        </div>
                    )}

                    {!isLoading && filteredTemplates.length === 0 && (
                        <div className="rounded-lg border border-line px-4 py-8 text-center text-sm text-ink-muted">
                            No templates match the current search.
                        </div>
                    )}

                    {!isLoading && filteredTemplates.length > 0 && (
                        <div className="grid gap-2">
                            {filteredTemplates.map((template) => (
                                <TemplateCard
                                    key={templateKey(template)}
                                    template={template}
                                    onUseTemplate={onUseTemplate}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
                    <div className="text-xs text-ink-muted">Manage your library in the Templates tab.</div>
                    <button
                        type="button"
                        onClick={onManageTemplates}
                        className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm font-semibold text-brand-ink transition-colors hover:border-brand-focus hover:text-brand-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-focus"
                    >
                        <PhoneHQGlyph name="settings" size={16}/>
                        Manage templates
                    </button>
                </div>
            </div>
        </div>
    );
};
